import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, ArrowLeft, Calendar, Clock } from 'lucide-react';

const Contact = () => {
  const [activeTab, setActiveTab] = useState('message'); // 'message' or 'appointment'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [appointmentData, setAppointmentData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    note: ''
  });
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAppointmentChange = (e) => {
    setAppointmentData({
      ...appointmentData,
      [e.target.name]: e.target.value
    });
  };

  const handleAppointmentSubmit = (e) => {
    e.preventDefault();
    
    // Create email subject and body
    const subject = `Showroom Appointment Request - ${appointmentData.name}`;
    const body = `
Hello,

I would like to schedule an appointment to visit the Artourius Design showroom.

Name: ${appointmentData.name}
Email: ${appointmentData.email}
Phone: ${appointmentData.phone}
Preferred Date: ${appointmentData.date}
Preferred Time: ${appointmentData.time}

Additional Note:
${appointmentData.note}

Looking forward to your confirmation.

Best regards,
${appointmentData.name}
    `.trim();

    // Open default email client with pre-filled data
    const mailtoLink = `mailto:artour.gevorken@agevo.ch?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;

    // Reset form
    setAppointmentData({
      name: '',
      email: '',
      phone: '',
      date: '',
      time: '',
      note: ''
    });
  };

  // Generate time slots
  const timeSlots = [];
  for (let hour = 9; hour <= 18; hour++) {
    for (let minute of ['00', '30']) {
      if (hour === 18 && minute === '30') continue; // Don't show 18:30
      const time = `${hour.toString().padStart(2, '0')}:${minute}`;
      timeSlots.push(time);
    }
  }

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: 'Address',
      lines: ['Artourius Design', 'Bahnhofplatz 3', '8001 Zürich'],
      delay: 0
    },
    {
      icon: Mail,
      label: 'Sales',
      lines: ['sales@artourius.design'],
      description: 'Ready to buy? Inquire about pricing, availability, and shipping.',
      link: 'mailto:sales@artourius.design',
      delay: 100
    },
    {
      icon: Mail,
      label: 'General Info',
      lines: ['info@artourius.design'],
      description: 'General questions? We\'re here to help.',
      link: 'mailto:info@artourius.design',
      delay: 150
    },
    {
      icon: Mail,
      label: 'Design Services',
      lines: ['design@artourius.design'],
      description: 'Transform your vision into reality. Collaborate with our designers to create bespoke pieces and comprehensive interior facelifts tailored to your living spaces.',
      link: 'mailto:design@artourius.design',
      delay: 200
    },
    {
      icon: Phone,
      label: 'Mobile',
      lines: ['+41 79 900 30 30'],
      link: 'tel:+41799003030',
      delay: 250
    }
  ];

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Back Button - Aligned with logo position */}
        <div className="mb-8 flex justify-center relative z-[60]">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-[hsl(45,100%,70%)] transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to home
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-16">
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Let's create something
              <br />
              <span className="text-[hsl(45,100%,70%)]">extraordinary together</span>
            </h1>
            <p className="text-gray-300 text-lg max-w-4xl mx-auto">
              Have a question or want to commission a custom piece? We'd love to hear from you.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <div
                  key={index}
                  className={`group transition-all duration-700 ${
                    isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'
                  }`}
                  style={{
                    transitionDelay: `${info.delay}ms`
                  }}
                >
                  <div className="flex items-start gap-6 p-6 rounded-2xl bg-gray-900/50 border border-gray-800 hover:border-[hsl(45,100%,70%)] transition-all duration-500 group-hover:bg-gray-900">
                    {/* Icon */}
                    <div className="w-16 h-16 rounded-full bg-[hsl(45,100%,70%)]/10 flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover:scale-110 group-hover:bg-[hsl(45,100%,70%)]/20">
                      <Icon className="w-8 h-8 text-[hsl(45,100%,70%)] transition-transform duration-500 group-hover:scale-110" />
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      {info.label && (
                        <h3 className="text-white font-medium mb-2 text-lg transition-colors duration-300 group-hover:text-[hsl(45,100%,70%)]">
                          {info.label}
                        </h3>
                      )}
                      {info.link ? (
                        <a
                          href={info.link}
                          className="block space-y-1 transition-transform duration-300 group-hover:translate-x-2"
                        >
                          {info.lines.map((line, i) => (
                            <p
                              key={i}
                              className="text-gray-300 group-hover:text-[hsl(45,100%,70%)] transition-colors text-lg font-medium"
                            >
                              {line}
                            </p>
                          ))}
                        </a>
                      ) : (
                        <div className="space-y-1 transition-transform duration-300 group-hover:translate-x-2">
                          {info.lines.map((line, i) => (
                            <p key={i} className="text-gray-300 text-lg">
                              {line}
                            </p>
                          ))}
                        </div>
                      )}
                      {info.description && (
                        <p className="text-gray-400 text-sm mt-3 leading-relaxed">
                          {info.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Map/Additional Info */}
            <div
              className={`transition-all duration-700 delay-300 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'
              }`}
            >
              <div className="p-8 rounded-2xl bg-gradient-to-br from-gray-900 to-black border border-gray-800">
                <h2 className="text-2xl font-bold text-white mb-4">Visit our showroom</h2>
                <p className="text-gray-400 mb-4 leading-relaxed">
                  Experience our pieces in person at our Zürich showroom. Visit by appointment to ensure personalized attention and guidance in selecting the perfect piece for your space.
                </p>
                <div className="flex flex-wrap gap-4 mt-6 mb-6">
                  <div className="px-4 py-2 bg-[hsl(45,100%,70%)]/10 border border-[hsl(45,100%,70%)]/30 rounded-full">
                    <span className="text-[hsl(45,100%,70%)] text-sm font-medium">By Appointment</span>
                  </div>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=Bahnhofplatz+3,+8001+Zürich,+Switzerland"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group px-4 py-2 bg-[hsl(45,100%,70%)]/10 border border-[hsl(45,100%,70%)]/30 rounded-full hover:bg-[hsl(45,100%,70%)]/20 hover:border-[hsl(45,100%,70%)]/50 transition-all duration-300 cursor-pointer hover:scale-105"
                  >
                    <span className="text-[hsl(45,100%,70%)] text-sm font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Zürich City Center
                    </span>
                  </a>
                </div>
                <button
                  onClick={() => setActiveTab('appointment')}
                  className="group w-full py-3 bg-[hsl(45,100%,70%)] text-black font-bold rounded-full hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
                >
                  <Calendar className="w-5 h-5" />
                  <span>Book Appointment</span>
                </button>
              </div>
            </div>
          </div>

          {/* Contact Form with Tabs */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'
            }`}
          >
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
              {/* Tabs */}
              <div className="flex gap-4 mb-8 border-b border-gray-800">
                <button
                  onClick={() => setActiveTab('message')}
                  className={`px-6 py-3 font-semibold transition-all duration-300 relative ${
                    activeTab === 'message'
                      ? 'text-[hsl(45,100%,70%)]'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Send a Message
                  {activeTab === 'message' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[hsl(45,100%,70%)]" />
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('appointment')}
                  className={`px-6 py-3 font-semibold transition-all duration-300 relative ${
                    activeTab === 'appointment'
                      ? 'text-[hsl(45,100%,70%)]'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Make an Appointment
                  {activeTab === 'appointment' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[hsl(45,100%,70%)]" />
                  )}
                </button>
              </div>

              {/* Message Form */}
              {activeTab === 'message' && (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="group">
                    <label className="block text-white font-medium mb-2 transition-colors duration-300 group-focus-within:text-[hsl(45,100%,70%)]">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white focus:border-[hsl(45,100%,70%)] focus:outline-none transition-all duration-300 focus:shadow-[0_0_15px_rgba(255,215,0,0.2)]"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="group">
                    <label className="block text-white font-medium mb-2 transition-colors duration-300 group-focus-within:text-[hsl(45,100%,70%)]">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white focus:border-[hsl(45,100%,70%)] focus:outline-none transition-all duration-300 focus:shadow-[0_0_15px_rgba(255,215,0,0.2)]"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="group">
                    <label className="block text-white font-medium mb-2 transition-colors duration-300 group-focus-within:text-[hsl(45,100%,70%)]">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white focus:border-[hsl(45,100%,70%)] focus:outline-none transition-all duration-300 focus:shadow-[0_0_15px_rgba(255,215,0,0.2)]"
                      placeholder="What's this about?"
                    />
                  </div>

                  <div className="group">
                    <label className="block text-white font-medium mb-2 transition-colors duration-300 group-focus-within:text-[hsl(45,100%,70%)]">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white focus:border-[hsl(45,100%,70%)] focus:outline-none transition-all duration-300 resize-none focus:shadow-[0_0_15px_rgba(255,215,0,0.2)]"
                      placeholder="Tell us more about your inquiry..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="group w-full py-4 bg-[hsl(45,100%,70%)] text-black font-bold rounded-full hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <span>Send Message</span>
                    <Send className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              )}

              {/* Appointment Form */}
              {activeTab === 'appointment' && (
                <form onSubmit={handleAppointmentSubmit} className="space-y-6">
                  <div className="group">
                    <label className="block text-white font-medium mb-2 transition-colors duration-300 group-focus-within:text-[hsl(45,100%,70%)]">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={appointmentData.name}
                      onChange={handleAppointmentChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white focus:border-[hsl(45,100%,70%)] focus:outline-none transition-all duration-300 focus:shadow-[0_0_15px_rgba(255,215,0,0.2)]"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="group">
                      <label className="block text-white font-medium mb-2 transition-colors duration-300 group-focus-within:text-[hsl(45,100%,70%)]">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={appointmentData.email}
                        onChange={handleAppointmentChange}
                        required
                        className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white focus:border-[hsl(45,100%,70%)] focus:outline-none transition-all duration-300 focus:shadow-[0_0_15px_rgba(255,215,0,0.2)]"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div className="group">
                      <label className="block text-white font-medium mb-2 transition-colors duration-300 group-focus-within:text-[hsl(45,100%,70%)]">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={appointmentData.phone}
                        onChange={handleAppointmentChange}
                        required
                        className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white focus:border-[hsl(45,100%,70%)] focus:outline-none transition-all duration-300 focus:shadow-[0_0_15px_rgba(255,215,0,0.2)]"
                        placeholder="+41 79 123 45 67"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="group">
                      <label className="flex items-center gap-2 text-white font-medium mb-2 transition-colors duration-300 group-focus-within:text-[hsl(45,100%,70%)]">
                        <Calendar className="w-4 h-4" />
                        Preferred Date *
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={appointmentData.date}
                        onChange={handleAppointmentChange}
                        required
                        min={getMinDate()}
                        className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white focus:border-[hsl(45,100%,70%)] focus:outline-none transition-all duration-300 focus:shadow-[0_0_15px_rgba(255,215,0,0.2)]"
                      />
                    </div>

                    <div className="group">
                      <label className="flex items-center gap-2 text-white font-medium mb-2 transition-colors duration-300 group-focus-within:text-[hsl(45,100%,70%)]">
                        <Clock className="w-4 h-4" />
                        Preferred Time *
                      </label>
                      <select
                        name="time"
                        value={appointmentData.time}
                        onChange={handleAppointmentChange}
                        required
                        className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white focus:border-[hsl(45,100%,70%)] focus:outline-none transition-all duration-300 focus:shadow-[0_0_15px_rgba(255,215,0,0.2)]"
                      >
                        <option value="">Select a time</option>
                        {timeSlots.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="group">
                    <label className="block text-white font-medium mb-2 transition-colors duration-300 group-focus-within:text-[hsl(45,100%,70%)]">
                      Additional Notes
                    </label>
                    <textarea
                      name="note"
                      value={appointmentData.note}
                      onChange={handleAppointmentChange}
                      rows={4}
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white focus:border-[hsl(45,100%,70%)] focus:outline-none transition-all duration-300 resize-none focus:shadow-[0_0_15px_rgba(255,215,0,0.2)]"
                      placeholder="Any specific pieces you'd like to view or questions you have..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="group w-full py-4 bg-[hsl(45,100%,70%)] text-black font-bold rounded-full hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <Calendar className="w-5 h-5" />
                    <span>Request Appointment</span>
                  </button>

                  <p className="text-sm text-gray-400 text-center">
                    This will open your email client with a pre-filled appointment request.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;